import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { LoadingBlockComponent } from '../../../shared/ui/loading-block/loading-block.component';
import { EmptyStateComponent } from '../../../shared/ui/empty-state/empty-state.component';
import { AuthService } from '../../../core/auth/auth.service';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import {
  AppointmentFormComponent,
  AppointmentFormPrefill,
  AppointmentSlotScopeChange,
} from './appointment-form.component';
import { AppointmentsService } from '../data-access/appointments.service';
import {
  AppointmentSpecialistSuggestionDto,
  ClientAppointmentCreateContextDto,
  CreateAppointmentRequest,
} from '../data-access/appointments.models';
import { ProcedureTypeItem } from '../../procedure-types/data-access/procedure-types.models';
import { ProcedureTypesService } from '../../procedure-types/data-access/procedure-types.service';
import { SlotItem } from '../../slots/data-access/slots.models';
import { SlotsService } from '../../slots/data-access/slots.service';
import { ClientProfileService } from '../../clients/data-access/client-profile.service';
import { ClientProfile } from '../../clients/data-access/client-profile.models';
import { ClientsAdminService } from '../../clients/data-access/clients-admin.service';
import { ClientAdminItem } from '../../clients/data-access/clients-admin.models';
import { SettingsService } from '../../settings/data-access/settings.service';
import { ClinicOperationalSettingsDto } from '../../settings/data-access/settings.models';
import { DevicesService } from '../../devices/data-access/devices.service';
import { DeviceItem } from '../../devices/data-access/devices.models';
import { UsersService } from '../../users/data-access/users.service';
import { UserItem } from '../../users/data-access/users.models';

@Component({
  selector: 'app-appointment-create-flow',
  standalone: true,
  imports: [
    LoadingBlockComponent,
    EmptyStateComponent,
    AppointmentFormComponent,
    ReactiveFormsModule,
    TranslatePipe
  ],
  template: `
    @if (isClientProfileLoading) {
      <app-loading-block text="Loading profile..." />
    } @else if (clientProfileMissing) {
      <app-empty-state
        title="Profile not found"
        description="No client profile is linked to your account yet."
      />
    } @else {
      <app-appointment-form
        [isSubmitting]="isSubmitting"
        [errorText]="errorText"
        [prefill]="prefill"
        [defaultClientId]="selectedClientIdAfterQuickCreate"
        [canQuickCreateClient]="canQuickCreateClient"
        [procedureTypes]="procedureTypes"
        [clinicProcedureAssignments]="clinicProcedureAssignments"
        [availableSlots]="availableSlots"
        [clinics]="clinicOptions"
        [devices]="deviceOptions"
        [specialists]="specialistOptions"
        [clients]="clientOptions"
        [lockedClientId]="lockedClientId"
        [clientIntentMode]="isClientIntentMode"
        [availabilityCalendarMode]="isClientIntentMode || availabilityCalendarMode"
        [selectPrefilledSlotOnOpen]="selectPrefilledSlotOnOpen"
        [suggestedSpecialistUserId]="suggestedSpecialistUserId"
        [minCalendarDate]="clientMinCalendarDate"
        [showConfirmationRequired]="!isClientIntentMode"
        [submitLabel]="isClientIntentMode ? 'Request appointment' : null"
        [submittingLabel]="isClientIntentMode ? 'Requesting...' : null"
        (submitted)="handleSubmitted($event)"
        (slotScopeChanged)="handleSlotScopeChanged($event)"
        (quickCreateClientRequested)="openQuickCreateClientModal()"
      />
    }

    @if (isQuickClientModalOpen) {
      <div class="appointment-create-flow__modal-backdrop" (click)="closeQuickCreateClientModal()">
        <div class="appointment-create-flow__modal" (click)="$event.stopPropagation()">
          <h3 class="appointment-create-flow__modal-title">{{ 'Quick create client' | t }}</h3>

          @if (quickClientErrorText) {
            <div class="appointment-create-flow__error">{{ quickClientErrorText | t }}</div>
          }

          <form [formGroup]="quickClientForm" (ngSubmit)="submitQuickCreateClient()" class="appointment-create-flow__modal-grid">
            <label>
              {{ 'Full Name' | t }}
              <input type="text" formControlName="fullName" />
            </label>

            <label>
              {{ 'Phone' | t }}
              <input type="text" formControlName="phone" />
            </label>

            <label>
              {{ 'Email (optional)' | t }}
              <input type="email" formControlName="email" />
            </label>

            <label class="appointment-create-flow__checkbox">
              <input type="checkbox" formControlName="sendInviteNow" />
              {{ 'Send invite now' | t }}
            </label>

            <label class="appointment-create-flow__modal-field-wide">
              {{ 'Notes' | t }}
              <textarea formControlName="notes"></textarea>
            </label>

            <div class="appointment-create-flow__modal-actions">
              <button type="button" class="appointment-create-flow__action" (click)="closeQuickCreateClientModal()">
                {{ 'Cancel' | t }}
              </button>
              <button
                type="submit"
                class="appointment-create-flow__action appointment-create-flow__action--primary"
                [disabled]="quickClientForm.invalid || isQuickClientSubmitting"
              >
                {{ (isQuickClientSubmitting ? 'Creating...' : 'Create client') | t }}
              </button>
            </div>
          </form>
        </div>
      </div>
    }
  `,
  styles: [`
    .appointment-create-flow__modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(17, 24, 39, 0.45);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 50;
      padding: 16px;
    }

    .appointment-create-flow__modal {
      width: min(640px, 100%);
      background: #ffffff;
      border-radius: 12px;
      border: 1px solid #e5e7eb;
      padding: 16px;
      max-height: calc(100dvh - 32px);
      overflow: auto;
    }

    .appointment-create-flow__modal-title {
      margin: 0 0 12px;
      font-size: 18px;
      color: #111827;
    }

    .appointment-create-flow__modal-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
    }

    .appointment-create-flow__modal-grid label {
      display: flex;
      flex-direction: column;
      gap: 6px;
      font-size: 13px;
      color: #374151;
      font-weight: 600;
    }

    .appointment-create-flow__modal-grid input,
    .appointment-create-flow__modal-grid textarea {
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 8px 10px;
      font: inherit;
      box-sizing: border-box;
    }

    .appointment-create-flow__modal-field-wide {
      grid-column: 1 / -1;
    }

    .appointment-create-flow__modal-actions {
      grid-column: 1 / -1;
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 4px;
    }

    .appointment-create-flow__action {
      height: 36px;
      border: 1px solid #d1d5db;
      background: #ffffff;
      border-radius: 8px;
      padding: 0 12px;
      cursor: pointer;
    }

    .appointment-create-flow__action--primary {
      background: #2563eb;
      border-color: #2563eb;
      color: #ffffff;
    }

    .appointment-create-flow__error {
      margin-bottom: 10px;
      padding: 8px 10px;
      border-radius: 8px;
      border: 1px solid #fecaca;
      background: #fef2f2;
      color: #b91c1c;
      font-size: 13px;
    }

    .appointment-create-flow__checkbox {
      flex-direction: row;
      align-items: center;
      gap: 8px;
      align-self: end;
      font-weight: 500;
    }

    @media (max-width: 900px) {
      .appointment-create-flow__modal-grid {
        grid-template-columns: 1fr;
      }

      .appointment-create-flow__modal-actions {
        flex-direction: column-reverse;
      }

      .appointment-create-flow__modal-actions .appointment-create-flow__action {
        width: 100%;
      }
    }

    @media (max-width: 640px) {
      .appointment-create-flow__modal {
        padding: 14px;
        border-radius: 10px;
      }

      .appointment-create-flow__modal-grid input,
      .appointment-create-flow__modal-grid textarea {
        font-size: 16px;
      }
    }
  `]
})
export class AppointmentCreateFlowComponent implements OnInit, OnDestroy
{
  private static readonly EMAIL_REGEXP = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  private readonly appointmentsService = inject(AppointmentsService);
  private readonly procedureTypesService = inject(ProcedureTypesService);
  private readonly slotsService = inject(SlotsService);
  private readonly settingsService = inject(SettingsService);
  private readonly devicesService = inject(DevicesService);
  private readonly usersService = inject(UsersService);
  private readonly clientProfileService = inject(ClientProfileService);
  private readonly clientsAdminService = inject(ClientsAdminService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  @Input() public prefill: AppointmentFormPrefill = {};
  @Input() public navigateOnSuccess = true;
  @Input() public forceClientIntentMode = false;
  @Input() public availabilityCalendarMode = true;
  @Input() public selectPrefilledSlotOnOpen = false;

  @Output() public readonly created = new EventEmitter<void>();

  protected isSubmitting = false;
  protected errorText: string | null = null;
  protected procedureTypes: ProcedureTypeItem[] = [];
  protected availableSlots: SlotItem[] = [];
  protected clinicOptions: Array<{ id: string; label: string; timeZone?: string }> = [];
  protected deviceOptions: Array<{ id: string; label: string; deviceTypeId?: string; clinicId?: string }> = [];
  protected specialistOptions: Array<{ id: string; label: string; procedureTypeIds?: string[] }> = [];
  protected clientOptions: Array<{ id: string; label: string; title: string }> = [];
  protected clinicProcedureAssignments: Record<string, string[]> = {};
  protected lockedClientId: string | null = null;
  protected isClientIntentMode = false;
  protected clientMinCalendarDate: string | null = null;
  protected isClientProfileLoading = false;
  protected clientProfileMissing = false;
  protected canQuickCreateClient = false;
  protected isQuickClientModalOpen = false;
  protected isQuickClientSubmitting = false;
  protected quickClientErrorText: string | null = null;
  protected selectedClientIdAfterQuickCreate: string | null = null;
  protected suggestedSpecialistUserId: string | null = null;
  private clientCatalogContext: ClientAppointmentCreateContextDto | null = null;
  private currentSlotScopeKey = '';
  private currentSpecialistSuggestionKey = '';
  private slotLoadSubscription: Subscription | null = null;
  private specialistSuggestionSubscription: Subscription | null = null;

  protected readonly quickClientForm = this.formBuilder.group({
    fullName: ['', [Validators.required, Validators.maxLength(255)]],
    phone: ['', [Validators.required]],
    email: ['', [Validators.email]],
    sendInviteNow: [false],
    notes: ['']
  });

  public ngOnInit(): void
  {
    const currentRole = this.authService.getCurrentRole();
    this.isClientIntentMode = this.forceClientIntentMode || currentRole === 'client';
    this.clientMinCalendarDate = currentRole === 'client' ? this.todayDateKey() : null;
    this.canQuickCreateClient =
      currentRole === 'system_admin'
      || currentRole === 'company_admin'
      || currentRole === 'manager'
      || currentRole === 'reception';

    this.initClientContext();
    this.loadReferenceOptions();
    this.loadProcedureTypes();
  }

  public ngOnDestroy(): void
  {
    this.slotLoadSubscription?.unsubscribe();
    this.specialistSuggestionSubscription?.unsubscribe();
  }

  public handleSubmitted(payload: CreateAppointmentRequest): void
  {
    this.isSubmitting = true;
    this.errorText = null;

    this.appointmentsService.create(payload).subscribe({
      next: () =>
      {
        this.created.emit();
        if (this.navigateOnSuccess) {
          void this.router.navigate(this.withTenantPath(['appointments']));
          return;
        }

        this.isSubmitting = false;
      },
      error: (error: unknown) =>
      {
        console.error('Failed to create appointment', error);
        this.errorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenAppointmentCreate',
          conflictByErrorCode: {
            appointment_client_time_overlap: 'error.appointmentClientTimeOverlap'
          },
          fallback: 'Backend rejected appointment creation request.'
        });
        this.isSubmitting = false;
      }
    });
  }

  public openQuickCreateClientModal(): void
  {
    if (!this.canQuickCreateClient) {
      return;
    }

    this.quickClientErrorText = null;
    this.quickClientForm.reset({
      fullName: '',
      phone: '',
      email: '',
      sendInviteNow: false,
      notes: ''
    });
    this.isQuickClientModalOpen = true;
  }

  public closeQuickCreateClientModal(): void
  {
    this.isQuickClientModalOpen = false;
    this.isQuickClientSubmitting = false;
    this.quickClientErrorText = null;
  }

  public submitQuickCreateClient(): void
  {
    if (this.quickClientForm.invalid || this.isQuickClientSubmitting) {
      this.quickClientForm.markAllAsTouched();
      return;
    }

    this.quickClientErrorText = null;
    this.isQuickClientSubmitting = true;

    const value = this.quickClientForm.getRawValue();
    const normalizedEmail = value.email?.trim().toLowerCase() ?? '';
    const shouldSendInvite = !!value.sendInviteNow;

    if (shouldSendInvite && !normalizedEmail) {
      this.quickClientErrorText = 'Email is required to send invite immediately.';
      this.isQuickClientSubmitting = false;
      return;
    }

    if (normalizedEmail && !AppointmentCreateFlowComponent.EMAIL_REGEXP.test(normalizedEmail)) {
      this.quickClientErrorText = 'Enter a valid email address.';
      this.isQuickClientSubmitting = false;
      return;
    }

    this.clientsAdminService.createIntake({
      fullName: value.fullName?.trim() ?? '',
      phone: value.phone?.trim() ?? '',
      email: normalizedEmail || undefined,
      notes: value.notes?.trim() || undefined,
    }).subscribe({
      next: (created) =>
      {
        const finalizeCreatedClient = (inviteFailed = false): void => {
          const label = this.buildQuickClientDisplayLabel(created.fullName, created.email);
          const title = this.buildQuickClientDisplayTitle(
            created.fullName,
            created.email,
            created.phone,
          );

          const deduped = this.clientOptions.filter((item) => item.id !== created.id);
          this.clientOptions = [{ id: created.id, label, title }, ...deduped];
          this.selectedClientIdAfterQuickCreate = created.id;
          this.isQuickClientSubmitting = false;
          this.isQuickClientModalOpen = false;

          if (inviteFailed) {
            this.errorText = 'Client created, but invite could not be sent.';
          }
        };

        if (!shouldSendInvite) {
          finalizeCreatedClient();
          return;
        }

        this.clientsAdminService.sendInvite(created.id, {
          email: normalizedEmail || undefined,
        }).subscribe({
          next: () => finalizeCreatedClient(),
          error: () => finalizeCreatedClient(true),
        });
      },
      error: (error: unknown) =>
      {
        this.quickClientErrorText = resolveHttpErrorMessageKey(error, {
          conflict: 'Client with same contact details already exists.',
          fallback: 'Failed to create client.'
        });
        this.isQuickClientSubmitting = false;
      }
    });
  }

  private withTenantPath(segments: Array<string>): Array<string>
  {
    const role = this.authService.getCurrentRole();
    const slug = this.authService.getPreferredCompanySlug();

    if (role !== 'system_admin' && slug)
    {
      return ['/', slug, ...segments];
    }

    return ['/', ...segments];
  }

  private loadProcedureTypes(): void
  {
    if (this.authService.getCurrentRole() === 'client') {
      return;
    }

    this.procedureTypesService.getList().subscribe({
      next: (items: ProcedureTypeItem[]) =>
      {
        this.procedureTypes = items;
      },
      error: (error: unknown) =>
      {
        console.error('Failed to load procedure types', error);
      }
    });
  }

  protected handleSlotScopeChanged(scope: AppointmentSlotScopeChange): void
  {
    this.handleSpecialistSuggestionScope(scope);

    const procedureTypeId = scope.procedureTypeId?.trim() ?? '';
    const schedulingDate = scope.schedulingDate?.trim() ?? '';
    const clinicId = scope.clinicId?.trim() ?? '';
    if (
      !procedureTypeId ||
      !schedulingDate ||
      (!this.isClientIntentMode && !clinicId)
    ) {
      this.slotLoadSubscription?.unsubscribe();
      this.availableSlots = [];
      this.currentSlotScopeKey = '';
      return;
    }

    const nextScopeKey = [
      clinicId,
      schedulingDate,
      procedureTypeId,
      scope.specialistUserId?.trim() ?? '',
      scope.deviceId?.trim() ?? '',
      this.isClientIntentMode ? 'client-intent' : 'create',
    ].join('|');

    if (nextScopeKey === this.currentSlotScopeKey) {
      return;
    }

    this.currentSlotScopeKey = nextScopeKey;
    this.loadRelevantSlots(scope);
  }

  private handleSpecialistSuggestionScope(scope: AppointmentSlotScopeChange): void
  {
    const clientId = scope.clientId?.trim() ?? '';
    const procedureTypeId = scope.procedureTypeId?.trim() ?? '';
    const clinicId = scope.clinicId?.trim() ?? '';

    if (!clientId || !procedureTypeId) {
      this.specialistSuggestionSubscription?.unsubscribe();
      this.currentSpecialistSuggestionKey = '';
      this.suggestedSpecialistUserId = null;
      return;
    }

    const nextSuggestionKey = [
      clientId,
      procedureTypeId,
      clinicId,
      this.isClientIntentMode ? 'client-intent' : 'create',
    ].join('|');

    if (nextSuggestionKey === this.currentSpecialistSuggestionKey) {
      return;
    }

    this.currentSpecialistSuggestionKey = nextSuggestionKey;
    this.specialistSuggestionSubscription?.unsubscribe();
    this.suggestedSpecialistUserId = null;

    this.specialistSuggestionSubscription = this.appointmentsService.getSpecialistSuggestion({
      clientId,
      procedureTypeId,
      clinicId: clinicId || undefined,
    }).subscribe({
      next: (suggestion: AppointmentSpecialistSuggestionDto) =>
      {
        if (this.currentSpecialistSuggestionKey !== nextSuggestionKey) {
          return;
        }

        this.suggestedSpecialistUserId = suggestion.specialistSelectionMode === 'suggested'
          ? suggestion.suggestedSpecialistUserId
          : null;
      },
      error: (error: unknown) =>
      {
        if (this.currentSpecialistSuggestionKey !== nextSuggestionKey) {
          return;
        }

        console.error('Failed to load appointment specialist suggestion', error);
        this.suggestedSpecialistUserId = null;
      }
    });
  }

  private loadRelevantSlots(scope: AppointmentSlotScopeChange): void
  {
    this.slotLoadSubscription?.unsubscribe();
    this.availableSlots = [];
    const expectedScopeKey = this.currentSlotScopeKey;

    const filters = {
      clinicId: scope.clinicId,
      procedureTypeId: scope.procedureTypeId,
      schedulingDate: scope.schedulingDate,
    };
    const specialistUserId = scope.specialistUserId?.trim() ?? '';
    const deviceId = scope.deviceId?.trim() ?? '';
    const selectedProcedure = this.procedureTypes.find(
      (item) => item.id === scope.procedureTypeId,
    );
    const requiresDevice = !!selectedProcedure?.deviceTypeId;
    const compatibleDeviceIds = requiresDevice
      ? this.deviceOptions
          .filter((item) =>
            item.deviceTypeId === selectedProcedure?.deviceTypeId &&
            (!scope.clinicId || !item.clinicId || item.clinicId === scope.clinicId),
          )
          .map((item) => item.id)
      : [];
    const requestedDeviceIds = deviceId
      ? compatibleDeviceIds.filter((id) => id === deviceId)
      : compatibleDeviceIds;
    const canLoadResourceStreams = !!specialistUserId && (!requiresDevice || compatibleDeviceIds.length > 0);
    const slotRequests = canLoadResourceStreams
      ? [
          this.slotsService.getList({
            ...filters,
            specialistUserId,
          }),
          ...requestedDeviceIds.map((id) =>
            this.slotsService.getList({
              ...filters,
              deviceId: id,
            })
          ),
        ]
      : [
          this.slotsService.getList(filters),
        ];

    this.slotLoadSubscription = forkJoin(slotRequests).pipe(
      map((slotGroups) => {
        const byId = new Map<string, SlotItem>();
        for (const slots of slotGroups) {
          for (const slot of slots) {
            byId.set(slot.id, slot);
          }
        }

        return Array.from(byId.values()).sort((left, right) =>
          left.startTime.localeCompare(right.startTime),
        );
      }),
    ).subscribe({
      next: (items: SlotItem[]) =>
      {
        if (this.currentSlotScopeKey !== expectedScopeKey) {
          return;
        }

        this.availableSlots = items;
        if (this.authService.getCurrentRole() === 'client') {
          this.applyClientCatalogSlotScope(items);
        }
      },
      error: (error: unknown) =>
      {
        if (this.currentSlotScopeKey !== expectedScopeKey) {
          return;
        }

        console.error('Failed to load slots for appointment creation', error);
      }
    });
  }

  private loadReferenceOptions(): void
  {
    if (this.authService.getCurrentRole() === 'client') {
      this.appointmentsService.getClientCreateContext().subscribe({
        next: (context) =>
        {
          this.clientCatalogContext = context;
          if (this.authService.getCurrentRole() === 'client') {
            const timeZones = Array.from(new Set(
              context.clinics
                .map((clinic) => clinic.timeZone?.trim() ?? '')
                .filter((timeZone) => timeZone.length > 0),
            ));
            this.clientMinCalendarDate = this.todayDateKey(timeZones.length === 1 ? timeZones[0] : undefined);
          }
          this.procedureTypes = context.procedureTypes.map((item) => ({
            id: item.id,
            name: item.name,
            deviceTypeId: item.deviceTypeId,
            defaultDurationMinutes: item.defaultDurationMinutes,
            isActive: item.isActive,
          }));
          this.applyClientCatalogSlotScope(this.availableSlots);
        },
        error: (error: unknown) =>
        {
          console.error('Failed to load client appointment create context', error);
          this.clientCatalogContext = null;
          this.clinicOptions = [];
          this.deviceOptions = [];
          this.specialistOptions = [];
          this.clinicProcedureAssignments = {};
        }
      });
      return;
    }

    this.settingsService.getClinicOperationalSettings().subscribe({
      next: (items: ClinicOperationalSettingsDto[]) =>
      {
        this.clinicOptions = items.map((item) => ({
          id: item.clinicId,
          label: item.clinicName,
          timeZone: item.timeZone,
        }));
        this.loadClinicProcedureAssignments(items.map((item) => item.clinicId));
      },
      error: () =>
      {
        this.clinicOptions = [];
        this.clinicProcedureAssignments = {};
      }
    });

    this.devicesService.getList().subscribe({
      next: (items: DeviceItem[]) =>
      {
        this.deviceOptions = items
          .filter((item) => item.status === 'active')
          .map((item) => ({
            id: item.id,
            deviceTypeId: item.deviceTypeId,
            clinicId: item.clinicId,
            label: [item.brand, item.model, item.serialNumber]
              .map((part) => part?.trim())
              .filter((part): part is string => !!part)
              .join(' / ') || item.id
          }));
      },
      error: () =>
      {
        this.deviceOptions = [];
      }
    });

    forkJoin({
      users: this.usersService.getList().pipe(
        catchError(() => of([] as UserItem[])),
      ),
      clients: this.clientsAdminService.getList().pipe(
        catchError(() => of([] as ClientAdminItem[])),
      ),
    }).subscribe({
      next: ({ users, clients }) =>
      {
        this.specialistOptions = users
          .filter((item) => item.role === 'specialist' && item.isActive)
          .map((item) => ({
            id: item.id,
            label: item.email,
            procedureTypeIds: item.specialistProcedureTypeIds ?? []
          }));

        const userBackedClientOptions = users
          .filter((item) => item.role === 'client' && item.isActive)
          .flatMap((item) =>
          {
            const clientProfileId = item.clientProfile?.id?.trim();
            if (!clientProfileId) {
              return [];
            }

            return [{
              id: clientProfileId,
              label: this.buildClientDisplayLabel(item),
              title: this.buildClientDisplayTitle(item),
            }];
          });

        const offlineClientOptions = clients
          .filter((item) => !item.userId?.trim())
          .map((item) => ({
            id: item.id,
            label: item.fullName.trim() || this.readableId(item.id),
            title: item.email?.trim() || item.phone?.trim() || item.fullName.trim() || item.id,
          }));

        this.clientOptions = this.deduplicateClientOptions([
          ...userBackedClientOptions,
          ...offlineClientOptions,
        ]);
      },
      error: () =>
      {
        this.specialistOptions = [];
        this.clientOptions = [];
      }
    });
  }

  private loadClinicProcedureAssignments(clinicIds: string[]): void
  {
    if (clinicIds.length === 0) {
      this.clinicProcedureAssignments = {};
      return;
    }

    forkJoin(
      clinicIds.map((clinicId) =>
        this.settingsService.getClinicProcedureAssignments(clinicId).pipe(
          map((items) =>
            [clinicId, items.filter((item) => item.assigned).map((item) => item.procedureTypeId)] as const
          ),
          catchError(() => of([clinicId, []] as const))
        )
      )
    ).subscribe({
      next: (entries) =>
      {
        this.clinicProcedureAssignments = entries.reduce<Record<string, string[]>>(
          (acc, [clinicId, ids]) =>
          {
            acc[clinicId] = Array.from(ids);
            return acc;
          },
          {}
        );
      },
      error: () =>
      {
        this.clinicProcedureAssignments = {};
      }
    });
  }

  private initClientContext(): void
  {
    const currentRole = this.authService.getCurrentRole();

    if (currentRole !== 'client')
    {
      return;
    }

    this.isClientProfileLoading = true;
    this.clientProfileMissing = false;

    this.clientProfileService.getMyProfile().subscribe({
      next: (profile: ClientProfile | null) =>
      {
        if (!profile)
        {
          this.clientProfileMissing = true;
          this.lockedClientId = null;
          this.isClientProfileLoading = false;
          return;
        }

        this.lockedClientId = profile.id;
        this.clientOptions = [{
          id: profile.id,
          label: profile.fullName.trim() || 'You',
          title: profile.email?.trim() || profile.phone?.trim() || profile.fullName.trim() || 'You',
        }];
        this.isClientProfileLoading = false;
      },
      error: (error: unknown) =>
      {
        console.error('Failed to load client profile for appointment create', error);
        this.errorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenAppointmentCreate',
          fallback: 'Failed to load profile'
        });
        this.isClientProfileLoading = false;
      }
    });
  }

  private buildClientDisplayLabel(item: UserItem): string
  {
    const fullName = item.clientProfile?.fullName?.trim() ?? '';
    return fullName || item.email.trim();
  }

  private buildClientDisplayTitle(item: UserItem): string
  {
    return item.email.trim() || item.clientProfile?.fullName?.trim() || item.id;
  }

  private buildQuickClientDisplayLabel(fullName: string, email: string | null): string
  {
    const normalizedName = fullName.trim();
    return normalizedName || (email?.trim() ?? '');
  }

  private buildQuickClientDisplayTitle(
    fullName: string,
    email: string | null,
    phone: string | null,
  ): string
  {
    return email?.trim() || phone?.trim() || fullName.trim();
  }

  private readableId(value: string | null | undefined): string
  {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    if (normalized.length > 12) {
      return `${normalized.slice(0, 8)}...`;
    }

    return normalized;
  }

  private applyClientCatalogSlotScope(items: SlotItem[]): void
  {
    const context = this.clientCatalogContext;
    if (!context) {
      return;
    }

    const clinicIdsWithSlots = new Set(
      items
        .map((item) => item.clinicId?.trim() ?? '')
        .filter((value) => value.length > 0),
    );
    const specialistIdsWithSlots = new Set(
      items
        .filter((item) => !item.deviceId)
        .map((item) => item.specialistUserId?.trim() ?? '')
        .filter((value) => value.length > 0),
    );
    const deviceIdsWithSlots = new Set(
      items
        .map((item) => item.deviceId?.trim() ?? '')
        .filter((value) => value.length > 0),
    );

    this.clinicOptions = clinicIdsWithSlots.size > 0
      ? context.clinics.filter((item) => clinicIdsWithSlots.has(item.id))
      : context.clinics;
    this.specialistOptions = specialistIdsWithSlots.size > 0
      ? context.specialists.filter((item) => specialistIdsWithSlots.has(item.id))
      : context.specialists;
    const contextDevices = deviceIdsWithSlots.size > 0
      ? context.devices.filter((item) => deviceIdsWithSlots.has(item.id))
      : context.devices;
    this.deviceOptions = contextDevices.map((item) => ({
      id: item.id,
      label: item.label,
      deviceTypeId: item.deviceTypeId ?? undefined,
      clinicId: item.clinicId ?? undefined,
    }));
    this.clinicProcedureAssignments = this.clinicOptions.reduce<Record<string, string[]>>((acc, clinic) => {
      acc[clinic.id] = context.clinicProcedureAssignments[clinic.id] ?? [];
      return acc;
    }, {});
  }

  private deduplicateClientOptions(
    items: Array<{ id: string; label: string; title: string }>,
  ): Array<{ id: string; label: string; title: string }>
  {
    const seen = new Set<string>();
    return items.filter((item) => {
      const normalizedId = item.id.trim();
      if (!normalizedId || seen.has(normalizedId)) {
        return false;
      }

      seen.add(normalizedId);
      return true;
    });
  }

  private todayDateKey(timeZone?: string): string
  {
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone,
    }).format(new Date());
  }
}
